/**
 * @module components/library/LibraryPanel
 * @summary Left shape palette: searchable, categorised, with live thumbnails.
 * @description Items support BOTH desktop drag-to-canvas (HTML5 DnD) and
 *   tap-to-place (click → drop at viewport centre). Right-click an item for
 *   context actions (move to category, delete from library).
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { engine } from '../../engine/CanvasEngine';
import { registry } from '../../engine/shapes/registry';
import { setCtx } from '../../engine/shapes/drawFns';
import { SHAPE_DND_TYPE } from '../canvas/CanvasView';
import { useUiStore } from '../../state/uiStore';
import {
  getAllCategories,
  isCatActive,
  getDisplayName,
  getEffectiveCat,
  isSymbolHidden,
  deleteBuiltinSymbol,
  moveSymbolToCat,
} from '../../lib/categories';
import type { ShapeDef } from '../../types';

interface LibCtxMenu {
  x: number;
  y: number;
  type: string;
  def: ShapeDef;
  isCustom: boolean;
}

/** A single draggable / tappable library tile with a rendered thumbnail. */
function LibraryItem({
  type,
  def,
  onContextMenu,
}: {
  type: string;
  def: ShapeDef;
  onContextMenu: (e: React.MouseEvent, type: string, def: ShapeDef) => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    const draw = registry.getDraw(type);
    if (!draw) return;
    const pad = 5;
    const sc = Math.min((c.width - pad * 2) / def.w, (c.height - pad * 2) / def.h, 1.3);
    ctx.save();
    ctx.translate((c.width - def.w * sc) / 2, (c.height - def.h * sc) / 2);
    ctx.scale(sc, sc);
    const s = {
      id: '_thumb',
      type,
      x: 0,
      y: 0,
      w: def.w,
      h: def.h,
      label: '',
      tag: '',
      labelPos: 'bottom' as const,
      fillColor: '#ffffff',
      strokeColor: '#1a1a1a',
      strokeWidth: 1.5,
      fontSize: 10,
      notes: '',
      zIndex: 0,
    };
    setCtx(ctx, s);
    try {
      draw(ctx, s);
    } catch {
      /* ignore a broken custom symbol thumbnail */
    }
    ctx.restore();
  }, [type, def]);

  return (
    <div
      className="lib-item"
      title={`${def.label} — drag to canvas or click to place`}
      draggable
      onDragStart={(e) => e.dataTransfer.setData(SHAPE_DND_TYPE, type)}
      onClick={() => engine.addShapeAtCenter(type)}
      onContextMenu={(e) => { e.preventDefault(); onContextMenu(e, type, def); }}
    >
      <canvas ref={ref} width={64} height={40} />
      <span>{def.label}</span>
    </div>
  );
}

export function LibraryPanel() {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [ctxMenu, setCtxMenu] = useState<LibCtxMenu | null>(null);
  const [moveToCat, setMoveToCat] = useState('');

  const libVersion = useUiStore((s) => s.libVersion);
  const bumpLibrary = useUiStore((s) => s.bumpLibrary);

  // Close context menu on outside click.
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [ctxMenu]);

  const openContextMenu = useCallback((e: React.MouseEvent, type: string, def: ShapeDef) => {
    setMoveToCat('');
    setCtxMenu({
      x: e.clientX,
      y: e.clientY,
      type,
      def,
      isCustom: registry.isCustom(type),
    });
  }, []);

  // Group all (built-in + custom) defs by effective category, filtering hidden/inactive.
  const grouped = useMemo(() => {
    const defs = registry.allDefs();
    const byCat = new Map<string, { type: string; def: ShapeDef }[]>();

    for (const [type, def] of Object.entries(defs)) {
      if (isSymbolHidden(type)) continue; // skip deleted built-ins
      const effectiveCat = getEffectiveCat(type, def.g);
      if (!byCat.has(effectiveCat)) byCat.set(effectiveCat, []);
      byCat.get(effectiveCat)!.push({ type, def: { ...def, g: effectiveCat } });
    }

    const allCats = getAllCategories();
    const order = [...allCats];
    // Append any categories that appeared via catMoves but aren't in the standard list.
    for (const cat of byCat.keys()) {
      if (!order.includes(cat)) order.push(cat);
    }

    return order
      .filter((c) => byCat.has(c) && isCatActive(c))
      .map((cat) => ({
        cat,
        displayName: getDisplayName(cat),
        items: byCat.get(cat)!,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libVersion]);

  // All categories for the "move to" dropdown (excluding current).
  const allCats = useMemo(() => getAllCategories(), [libVersion]);

  const q = search.trim().toLowerCase();
  const toggle = (cat: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });

  function handleDelete() {
    if (!ctxMenu) return;
    const { type, def, isCustom } = ctxMenu;
    if (!confirm(`Remove "${def.label}" from the library?`)) return;
    if (isCustom) {
      registry.removeCustom(type);
    } else {
      deleteBuiltinSymbol(type);
    }
    bumpLibrary();
    setCtxMenu(null);
  }

  function handleMoveTo(targetCat: string) {
    if (!ctxMenu || !targetCat) return;
    const { type, isCustom } = ctxMenu;
    if (isCustom) {
      // For custom symbols update the stored record's category.
      const recs = registry.customRecords();
      if (recs[type]) {
        registry.addCustom(type, { ...recs[type], g: targetCat });
      }
    } else {
      moveSymbolToCat(type, targetCat);
    }
    bumpLibrary();
    setCtxMenu(null);
  }

  return (
    <div id="library">
      <div id="lib-top">
        <input
          id="lib-search"
          placeholder="🔍 Search symbols…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div id="lib-scroll">
        {grouped.map(({ cat, displayName, items }) => {
          const visible = q
            ? items.filter(
                (it) =>
                  it.def.label.toLowerCase().includes(q) || it.type.toLowerCase().includes(q),
              )
            : items;
          if (!visible.length) return null;
          const isCollapsed = collapsed.has(cat) && !q;
          return (
            <div key={cat}>
              <div className="lib-grp-hdr" onClick={() => toggle(cat)}>
                <span>{displayName}</span>
                <span>{isCollapsed ? '+' : '−'}</span>
              </div>
              {!isCollapsed && (
                <div className="lib-items">
                  {visible.map((it) => (
                    <LibraryItem
                      key={it.type}
                      type={it.type}
                      def={it.def}
                      onContextMenu={openContextMenu}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Right-click context menu */}
      {ctxMenu && (
        <div
          className="lib-ctx-menu"
          style={{ top: ctxMenu.y, left: ctxMenu.x }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="lib-ctx-header">
            <span className="lib-ctx-label">{ctxMenu.def.label}</span>
            <span className="lib-ctx-type">{ctxMenu.type}</span>
          </div>
          <div className="ctx-sep" />

          {/* Move to category */}
          <div className="lib-ctx-move-row">
            <span className="lib-ctx-move-lbl">📁 Move to:</span>
            <select
              className="lib-ctx-cat-sel"
              aria-label="Select target category"
              value={moveToCat}
              onChange={(e) => setMoveToCat(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            >
              <option value="">— choose category —</option>
              {allCats
                .filter((c) => c !== getEffectiveCat(ctxMenu.type, ctxMenu.def.g))
                .map((c) => (
                  <option key={c} value={c}>{getDisplayName(c)}</option>
                ))}
            </select>
            <button
              type="button"
              className="lib-ctx-move-btn"
              disabled={!moveToCat}
              onClick={() => handleMoveTo(moveToCat)}
            >
              Move
            </button>
          </div>

          <div className="ctx-sep" />
          <div className="ctx-item danger" onClick={handleDelete}>
            🗑 Delete from Library
          </div>
        </div>
      )}
    </div>
  );
}
